import { Tooltip } from '@/components/tooltips';
import { FC, PropsWithChildren, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export type FilterDisabledTooltipSource = 'audience' | 'google' | 'meta';

const filterDisabledTooltipClassName =
  'z-[100] max-w-[340px] rounded-[10px] bg-[#E8ECF2] px-4 py-3.5 text-[13px] font-normal leading-[1.45] text-ink-800 shadow-none';

const filterDisabledTooltipTriggerClassName =
  'rounded-[10px] outline-none focus-visible:shadow-[0_0_0_3px_var(--ar-blue-soft)]';

type FilterDisabledTooltipProps = PropsWithChildren<{
  disabled: boolean;
  source: FilterDisabledTooltipSource;
}>;

export const FilterDisabledTooltip: FC<FilterDisabledTooltipProps> = ({ disabled, source, children }) => {
  const { t } = useTranslation('performanceAudience');
  const tooltipId = useId();

  const tooltipContent = useMemo(() => {
    if (source === 'audience') {
      return t('filters.disabledEmptyAudience');
    }

    const sourceName = source === 'google' ? t('filters.googleAds') : t('filters.meta');
    return t('filters.disabledEmptySource', { sourceName });
  }, [source, t]);

  if (!disabled) {
    return children;
  }

  return (
    <Tooltip
      id={tooltipId}
      tooltipContent={tooltipContent}
      className={filterDisabledTooltipClassName}
      triggerTabIndex={0}
      triggerClassName={filterDisabledTooltipTriggerClassName}
      triggerAriaDescribedBy={tooltipId}
      triggerAriaDisabled
      noArrow
      place='top'
    >
      {children}
    </Tooltip>
  );
};
